import tooltipIcon from 'assets/icons/info-deep-blue.svg';
import React, { useContext, useState } from 'react';
import { Field, FieldRenderProps, Form, FormRenderProps } from 'react-final-form';
import {
  Button,
  Checkbox,
  FormContent,
  Input,
  Link,
  LocaleContext,
  ReCaptchaPolicy,
  RefreshingReCaptcha,
  Select,
  Textarea,
  Tooltip,
  UploadSingleFileField,
} from 'shared';
import { NON_TPP, TPP, TPP_TO_BE } from 'store/api/types';
import { getEnvironmentVar } from 'utils';
import { makeCountrySelectOptions } from 'utils/countryList/countryList';
import {
  combineValidators,
  isRepeatTheSame,
  required as requiredValidator,
  validateBusinessRegistrationNumber,
  validateEmail,
  validatePassword,
} from 'utils/formValidators';

import {
  FieldWithTooltip,
  RegistrationFooter,
  RegistrationTooltip,
  TermsCheckboxLabel,
  UploadTestCert,
} from './RegistrationForm.styles';
import { RegistrationFormProps } from './RegistrationForm.types';

const formatSelectValue = (value?: any) => (value ? { value, label: value } : undefined);
const parseSelectValue = (value?: any) => (value ? value.value : '');

const ConditionField = ({ when, is, children }: { when: string; is: any; children: any }) => (
  <Field name={when} subscription={{ value: true }}>
    {({ input: { value } }) => (value === is ? children : null)}
  </Field>
);

export const RegistrationForm = ({ onSubmit, onShowLoginForm }: RegistrationFormProps) => {
  const localeContext = useContext(LocaleContext);
  const [isCaptchaFilled, setCaptchaFilled] = useState(false);
  const required = requiredValidator(localeContext.formatMessage({ id: 'FORM.REQUIRED' }));

  const handleVerifyCaptcha = (change: (name: string, value?: any) => void) => (response: string) => {
    change('captchaResponse', response);
    setCaptchaFilled(true);
  };

  const getProvisioningStatusLabel = (option: { label: string; value: string }) =>
    localeContext.formatMessage({ id: `REGISTRATION.FORM.${option.value}` });

  return (
    <FormContent className="form registration-form">
      <Form
        onSubmit={onSubmit}
        validateOnBlur={false}
        initialValues={{ country: 'Poland' }}
        subscription={{ submitting: true }}
      >
        {({ handleSubmit, submitting, form: { change } }: FormRenderProps) => {
          return (
            <form onSubmit={handleSubmit} action="">
              <Field name="name" validate={required}>
                {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                  <Input
                    label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.FIRSTNAME' })}
                    input={input}
                    meta={meta}
                    className="registration-form__first-name"
                    maxLenght={100}
                  />
                )}
              </Field>
              <Field name="surname" validate={required}>
                {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                  <Input
                    label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.LASTNAME' })}
                    input={input}
                    meta={meta}
                    className="registration-form__last-name"
                    maxLenght={100}
                  />
                )}
              </Field>
              <Field
                name="email"
                validate={combineValidators(
                  required,
                  validateEmail(localeContext.formatMessage({ id: 'FORM.INVALID-EMAIL' })),
                )}
              >
                {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                  <Input
                    label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.EMAIL' })}
                    input={input}
                    meta={meta}
                    className="registration-form__email"
                    maxLenght={100}
                  />
                )}
              </Field>

              <FieldWithTooltip>
                <Field
                  name="password"
                  validate={validatePassword(
                    localeContext.formatMessage({ id: 'REGISTRATION.FORM.PASSWORD-INCORRECT' }),
                  )}
                >
                  {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                    <Input
                      label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.PASSWORD' })}
                      input={{ ...input, type: 'password', autoComplete: 'new-password' }}
                      meta={meta}
                      className="registration-form__password"
                    />
                  )}
                </Field>
                <Tooltip
                  content={
                    <RegistrationTooltip>
                      {localeContext.formatMessage({ id: 'FORM.PASSWORD-TIP' })}
                    </RegistrationTooltip>
                  }
                >
                  {({ ref }) => <img ref={ref} src={tooltipIcon} alt="" />}
                </Tooltip>
              </FieldWithTooltip>

              <Field
                name="confirmPassword"
                validate={isRepeatTheSame(
                  localeContext.formatMessage({ id: 'CHANGE-PASSWORD.FORM.NOT-MATCH' }),
                  'password',
                )}
              >
                {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                  <Input
                    label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.CONFIRM-PASSWORD' })}
                    input={{ ...input, type: 'password', autoComplete: 'new-password' }}
                    meta={meta}
                    className="registration-form__repeat-password"
                  />
                )}
              </Field>

              <Field name="companyName" validate={required}>
                {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                  <Input
                    label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.COMPANY-NAME' })}
                    input={input}
                    meta={meta}
                    className="registration-form__company-name"
                    maxLenght={200}
                  />
                )}
              </Field>

              <FieldWithTooltip>
                <Field
                  name="nip"
                  validate={combineValidators(
                    required,
                    validateBusinessRegistrationNumber(
                      localeContext.formatMessage({ id: 'FORM.INVALID-BUSINESS-REGISTRATION-NUMBER' }),
                    ),
                  )}
                >
                  {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                    <Input
                      label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.BUSINESS-REGISTRATION-NUMBER' })}
                      input={input}
                      meta={meta}
                      className="registration-form__nip"
                      maxLenght={14}
                    />
                  )}
                </Field>
                <Tooltip
                  content={
                    <RegistrationTooltip>
                      {localeContext.formatMessage({ id: 'FORM.BUSINESS-REGISTRATION-NUMBER-TIP' })}
                    </RegistrationTooltip>
                  }
                >
                  {({ ref }) => <img ref={ref} src={tooltipIcon} alt="" />}
                </Tooltip>
              </FieldWithTooltip>

              <Field name="country" validate={required} format={formatSelectValue} parse={parseSelectValue}>
                {({ input, meta }: { meta: FieldRenderProps<any>['meta']; input: any }) => (
                  <Select
                    placeholder={localeContext.formatMessage({ id: 'REGISTRATION.FORM.COUNTRY' })}
                    input={{ ...input, isSearchable: true, onFocus: (e: any) => e.preventDefault() }}
                    meta={meta}
                    className="registration-form__country"
                    options={makeCountrySelectOptions()}
                    menuPlacement="top"
                  />
                )}
              </Field>

              <Field name="provisioningStatus" validate={required} format={formatSelectValue} parse={parseSelectValue}>
                {({ input, meta }: { meta: FieldRenderProps<any>['meta']; input: any }) => (
                  <Select
                    placeholder={localeContext.formatMessage({ id: 'REGISTRATION.FORM.TPP-PROVISIONING-STATUS' })}
                    input={{ ...input, isSearchable: true, onFocus: (e: any) => e.preventDefault() }}
                    meta={meta}
                    className="registration-form__provisioning"
                    options={[
                      {
                        label: localeContext.formatMessage({ id: 'REGISTRATION.FORM.TPP' }),
                        value: TPP,
                      },
                      {
                        label: localeContext.formatMessage({ id: 'REGISTRATION.FORM.TPP_TO_BE' }),
                        value: TPP_TO_BE,
                      },
                      {
                        label: localeContext.formatMessage({ id: 'REGISTRATION.FORM.NON_TPP' }),
                        value: NON_TPP,
                      },
                    ]}
                    menuPlacement="top"
                    getOptionLabel={getProvisioningStatusLabel}
                  />
                )}
              </Field>

              <ConditionField when="provisioningStatus" is={NON_TPP}>
                <FieldWithTooltip type="textarea">
                  <Field name="description" validate={required}>
                    {({ input, meta }: FieldRenderProps<HTMLTextAreaElement>) => (
                      <Textarea
                        label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.DESCRIPTION' })}
                        input={input}
                        meta={meta}
                        className="registration-form__description"
                        maxLenght={400}
                        charsCounter={true}
                      />
                    )}
                  </Field>
                  <Tooltip
                    content={
                      <RegistrationTooltip>
                        {localeContext.formatMessage({ id: 'REGISTRATION.FORM.DESCRIPTION-TIP' })}
                      </RegistrationTooltip>
                    }
                  >
                    {({ ref }) => <img ref={ref} src={tooltipIcon} alt="" />}
                  </Tooltip>
                </FieldWithTooltip>
              </ConditionField>

              <ConditionField when="provisioningStatus" is="HIDDEN">
                <Field name="attachment" validate={required}>
                  {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                    <UploadSingleFileField
                      label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.UPLOAD-CERT' })}
                      input={input}
                      meta={meta}
                      className={`registration-form__attachment`}
                      allowedExtensions={[]}
                    />
                  )}
                </Field>
              </ConditionField>

              <ConditionField when="provisioningStatus" is="HIDDEN">
                <Field name="attachment" validate={required}>
                  {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                    <UploadTestCert>
                      <UploadSingleFileField
                        label={localeContext.formatMessage({ id: 'REGISTRATION.FORM.UPLOAD-TEST-CERT' })}
                        input={input}
                        meta={meta}
                        className={`registration-form__attachment`}
                        allowedExtensions={[]}
                      />
                      {!input.value && (
                        <Tooltip
                          content={
                            <RegistrationTooltip>
                              {localeContext.formatMessage({ id: 'REGISTRATION.FORM.UPLOAD-TEST-CERT-TIP' })}
                            </RegistrationTooltip>
                          }
                        >
                          {({ ref }) => <img ref={ref} src={tooltipIcon} alt="" />}
                        </Tooltip>
                      )}
                    </UploadTestCert>
                  )}
                </Field>
              </ConditionField>

              <Field name="termsOfUse" validate={required} type="checkbox">
                {({ input, meta }: { meta: FieldRenderProps<HTMLInputElement>['meta']; input: any }) => (
                  <Checkbox
                    label={
                      <TermsCheckboxLabel>
                        {localeContext.formatMessage({ id: 'REGISTRATION.FORM.TERMS' })}
                        <Link
                          external={true}
                          target="_blank"
                          url={`${getEnvironmentVar('REACT_APP_SPA_URL')}pages/service-policy`}
                        >
                          {localeContext.formatMessage({ id: 'REGISTRATION.FORM.TERMS-LINK' })}
                        </Link>
                      </TermsCheckboxLabel>
                    }
                    input={input}
                    meta={meta}
                    className="registration-form__terms"
                  />
                )}
              </Field>

              <RefreshingReCaptcha refreshTime={90000} onVerify={handleVerifyCaptcha(change)} />

              <ReCaptchaPolicy />

              <RegistrationFooter>
                <Button
                  type="leading-flat"
                  onClick={onShowLoginForm}
                  htmlType="button"
                  className="registration-form__cancel"
                  disabled={submitting}
                >
                  {localeContext.formatMessage({ id: 'GENERAL.CANCEL' })}
                </Button>

                <Button type="leading" className="registration-form__submit" disabled={submitting || !isCaptchaFilled}>
                  {localeContext.formatMessage({ id: 'REGISTRATION.FORM.CONFIRM' })}
                </Button>
              </RegistrationFooter>
            </form>
          );
        }}
      </Form>
    </FormContent>
  );
};
